import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import FamilyHorseCareEditor from './FamilyHorseCareEditor.tsx';
import CogginsViewLink from '../shared/CogginsViewLink.tsx';
import { fetchHorseById } from '../../utils/horseCareApi.ts';
import { AppIcon, AppIconName } from '../../icons/appIcons.tsx';
import HorsePhotoPlaceholder from '../common/HorsePhotoPlaceholder.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Horse {
  id: string;
  name: string;
  breed?: string;
  age?: number;
  color?: string;
  stallNumber?: string;
  designation: string;
  status: string;
  temperament?: string;
  ridingLevel?: string;
  imageUrl?: string;
  specialNeeds?: string;
  medicalNotes?: string;
  acquisitionDate?: string;
  farrierName?: string;
  lastSeenByFarrier?: string;
  lastCoggins?: string;
  lastWormer?: string;
  lastRabies?: string;
  cogginsDocumentUrl?: string;
  cogginsDocumentOriginalName?: string;
  notes?: string;
  ownerFamily?: {
    id: string;
    familyName: string;
  };
  assignedRanchGirls?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    family?: {
      familyName: string;
    };
  }>;
}

interface RanchGirl {
  id: string;
  firstName: string;
  lastName: string;
  assignedHorse?: Horse;
  assignedHorseId?: string;
}

interface FamilyData {
  id: string;
  familyName: string;
  ranchGirls: RanchGirl[];
}

export default function HorsesView() {
  const { user } = useAuth();
  const [familyData, setFamilyData] = useState<FamilyData | null>(null);
  const [allHorses, setAllHorses] = useState<Horse[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAlumni, setIsAlumni] = useState(false);
  const [viewMode, setViewMode] = useState<'assigned' | 'all'>('all');
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null);
  const [selectedAssignedGirls, setSelectedAssignedGirls] = useState<RanchGirl[] | undefined>(undefined);
  const [editingCare, setEditingCare] = useState(false);
  const [loadingHorseDetail, setLoadingHorseDetail] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  useEffect(() => {
    // Check if user is alumni
    let isAlumniUser = false;
    try {
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        const user = JSON.parse(authUser);
        isAlumniUser = user.role === 'alumni';
      }
    } catch (error) {
      console.error('Error parsing authUser:', error);
    }
    
    setIsAlumni(isAlumniUser);
    fetchData(isAlumniUser);
  }, []);

  const canEditHorseCare = (horse: Horse) => {
    if (user?.role !== 'family' || !user.familyId || horse.designation !== 'personal horse') {
      return false;
    }
    if (horse.ownerFamily?.id === user.familyId) return true;
    return !!familyData?.ranchGirls?.some((g) => g.assignedHorse?.id === horse.id);
  };

  const mergeHorseUpdate = (horseId: string, patch: Partial<Horse>) => {
    setAllHorses(prev => prev.map(h => (h.id === horseId ? { ...h, ...patch } : h)));
    setSelectedHorse(prev => (prev && prev.id === horseId ? { ...prev, ...patch } : prev));
    setFamilyData(prev => {
      if (!prev?.ranchGirls) return prev;
      return {
        ...prev,
        ranchGirls: prev.ranchGirls.map(girl =>
          girl.assignedHorse?.id === horseId
            ? { ...girl, assignedHorse: { ...girl.assignedHorse!, ...patch } }
            : girl
        )
      };
    });
  };

  const mergeWithListedHorse = (horse: Horse): Horse => {
    const fromList = allHorses.find((h) => h.id === horse.id);
    return fromList ? { ...horse, ...fromList } : horse;
  };

  const loadFullHorse = async (horse: Horse): Promise<Horse> => {
    const merged = mergeWithListedHorse(horse);
    try {
      const full = await fetchHorseById(horse.id);
      return { ...merged, ...full };
    } catch {
      return merged;
    }
  };

  const openHorseDetails = async (horse: Horse, assignedGirls?: RanchGirl[]) => {
    setLoadingHorseDetail(true);
    setEditingCare(false);
    setSelectedAssignedGirls(assignedGirls);
    try {
      setSelectedHorse(await loadFullHorse(horse));
    } finally {
      setLoadingHorseDetail(false);
    }
  };

  const startEditingHorse = async () => {
    if (!selectedHorse) return;
    setLoadingHorseDetail(true);
    try {
      setSelectedHorse(await loadFullHorse(selectedHorse));
      setEditingCare(true);
    } finally {
      setLoadingHorseDetail(false);
    }
  };

  const fetchData = async (skipFamilyFetch = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      // Only fetch family profile for family users, not alumni
      if (!skipFamilyFetch) {
        const familyResponse = await fetch('/api/families/profile', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (familyResponse.ok) {
          const familyData = await familyResponse.json();
          if (familyData.success) {
            setFamilyData(familyData.data);
          }
        }
      }

      // Fetch all horses
      const horsesResponse = await fetch('/api/horses', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (horsesResponse.ok) {
        const horsesData = await horsesResponse.json();
        if (horsesData.success) {
          // Show all horses to family and alumni users
          setAllHorses(horsesData.data);
        }
      }
    } catch (error) {
      console.error('Error fetching horse data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'In herd':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'coming soon':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In isolation':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'do not ride':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDesignationBadgeColor = (designation: string) => {
    switch (designation) {
      case 'troop horse':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'personal horse':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'for lease':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const renderHorseCard = (horse: Horse, assignedTo?: RanchGirl, assignedGirls?: RanchGirl[]) => (
    <div key={horse.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-ggaa-green/20 hover:shadow-xl transition-shadow">
      {/* Horse Image */}
      <div className="relative h-64 bg-gradient-to-br from-ggaa-cream to-ggaa-green/10">
        {horse.imageUrl ? (
          <img
            src={horse.imageUrl}
            alt={horse.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0e9d8" width="100" height="200"/%3E%3C/svg%3E';
            }}
          />
        ) : (
          <HorsePhotoPlaceholder size="lg" />
        )}
        {assignedTo && (
          <div className="absolute top-3 right-3 bg-ggaa-green text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
            Assigned to {assignedTo.firstName}
          </div>
        )}
        {assignedGirls && assignedGirls.length > 0 && (
          <div className="absolute top-3 right-3 bg-ggaa-green text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
            {assignedGirls.length === 1 
              ? `Assigned to ${assignedGirls[0].firstName}`
              : `Assigned to ${assignedGirls.length} girls`
            }
          </div>
        )}
      </div>

      {/* Horse Details */}
      <div className="p-4 sm:p-6">
        <div className="flex justify-between items-start mb-3 gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{horse.name}</h3>
              {horse.stallNumber && (
                <span className="bg-ggaa-green text-white px-3 py-1 rounded-md text-sm font-semibold shadow-sm">
                  Stall {horse.stallNumber}
                </span>
              )}
            </div>
            {horse.breed && (
              <p className="text-gray-600">{horse.breed}</p>
            )}
          </div>
          {horse.age && (
            <span className="text-lg font-semibold text-ggaa-green">
              {horse.age} {horse.age === 1 ? 'year' : 'years'}
            </span>
          )}
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(horse.status)}`}>
            {horse.status}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getDesignationBadgeColor(horse.designation)}`}>
            {horse.designation.charAt(0).toUpperCase() + horse.designation.slice(1)}
          </span>
        </div>

        {/* Horse Info Grid */}
        <div className="space-y-2 text-sm">
          {horse.color && (
            <div className="flex items-start">
              <span className="font-medium text-gray-700 w-32">Color:</span>
              <span className="text-gray-600 flex-1">{horse.color}</span>
            </div>
          )}
          {horse.temperament && (
            <div className="flex items-start">
              <span className="font-medium text-gray-700 w-32">Temperament:</span>
              <span className="text-gray-600 flex-1">{horse.temperament}</span>
            </div>
          )}
          {horse.ridingLevel && (
            <div className="flex items-start">
              <span className="font-medium text-gray-700 w-32">Riding Level:</span>
              <span className="text-gray-600 flex-1">{horse.ridingLevel}</span>
            </div>
          )}
          {horse.specialNeeds && (
            <div className="flex items-start">
              <span className="font-medium text-gray-700 w-32">Special Needs:</span>
              <span className="text-gray-600 flex-1">{horse.specialNeeds}</span>
            </div>
          )}
          {horse.notes && (
            <div className="flex items-start mt-3 pt-3 border-t border-gray-200">
              <span className="font-medium text-gray-700 w-32">Notes:</span>
              <span className="text-gray-600 flex-1">{horse.notes}</span>
            </div>
          )}
          {assignedGirls && assignedGirls.length > 0 && (
            <div className="flex items-start mt-3 pt-3 border-t border-gray-200">
              <span className="font-medium text-gray-700 w-32">Assigned To:</span>
              <div className="flex-1">
                {assignedGirls.map((girl, index) => (
                  <div key={girl.id} className="text-gray-600 mb-1">
                    {girl.firstName} {girl.lastName}
                    {girl.family && <span className="text-gray-500 text-xs ml-2">({girl.family.familyName})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {(horse.lastCoggins || horse.lastWormer || horse.lastRabies || horse.cogginsDocumentUrl) && (
          <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600 space-y-1">
            {horse.lastCoggins && <div>Last Coggins: {formatDate(horse.lastCoggins)}</div>}
            {horse.lastWormer && <div>Last wormer: {formatDate(horse.lastWormer)}</div>}
            {horse.lastRabies && <div>Last rabies: {formatDate(horse.lastRabies)}</div>}
            {horse.cogginsDocumentUrl && (
              <CogginsViewLink horseId={horse.id} className="text-ggaa-green font-medium hover:underline">
                View Coggins certificate
              </CogginsViewLink>
            )}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => openHorseDetails(horse, assignedGirls)}
            className="w-full bg-ggaa-green text-white px-4 py-3 rounded-lg hover:bg-ggaa-green/90 transition-colors text-sm font-medium inline-flex items-center justify-center gap-2 min-h-[44px]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Details
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
        </div>
      </div>
    );
  }

  const assignedHorses = familyData?.ranchGirls
    ?.filter(girl => girl.assignedHorse)
    .map(girl => ({ horse: girl.assignedHorse!, girl })) || [];

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      {/* Header with View Toggle */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Our Horses</h1>
        
        {/* Only show tabs for family users, not alumni */}
        {!isAlumni && (
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:inline-flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setViewMode('all')}
              className={`px-4 py-3 sm:py-2 rounded-md text-sm font-medium transition-colors min-h-[44px] ${
                viewMode === 'all'
                  ? 'bg-white text-ggaa-green shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Horses
              <span className="ml-2 bg-ggaa-green text-white px-2 py-0.5 rounded-full text-xs">
                {allHorses.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('assigned')}
              className={`px-4 py-3 sm:py-2 rounded-md text-sm font-medium transition-colors min-h-[44px] ${
                viewMode === 'assigned'
                  ? 'bg-white text-ggaa-green shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Ranch Girl&apos;s Horse
              {assignedHorses.length > 0 && (
                <span className="ml-2 bg-ggaa-green text-white px-2 py-0.5 rounded-full text-xs">
                  {assignedHorses.length}
                </span>
              )}
            </button>
          </div>
        )}
        
        {/* For alumni, show a simple description */}
        {isAlumni && (
          <p className="text-gray-600">
            Browse all the horses at our ranch. These wonderful animals are part of our extended ranch family!
          </p>
        )}
      </div>

      {/* Assigned Horses View - Only for family users */}
      {!isAlumni && viewMode === 'assigned' && (
        <div>
          {assignedHorses.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <EmptyState
                icon="horse"
                title="No horses currently assigned"
                description="Your ranch girl will be assigned a horse soon!"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {assignedHorses.map(({ horse, girl }) => renderHorseCard(horse, girl, undefined))}
            </div>
          )}
        </div>
      )}

      {/* All Horses View */}
      {viewMode === 'all' && (
        <div>
          {allHorses.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <EmptyState icon="horse" title="No horses available to display" />
            </div>
          ) : (
            <>
              {!isAlumni && (
                <p className="text-gray-600 mb-6">
                  Browse all the horses at our ranch. These wonderful animals are part of our extended ranch family!
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allHorses.map(horse => {
                  // Get all assigned ranch girls for this horse
                  const assignedGirls = horse.assignedRanchGirls && horse.assignedRanchGirls.length > 0
                    ? horse.assignedRanchGirls.map(girl => ({
                        id: girl.id,
                        firstName: girl.firstName,
                        lastName: girl.lastName,
                        family: girl.family
                      }))
                    : undefined;
                  
                  return renderHorseCard(horse, undefined, assignedGirls);
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Horse Details Modal */}
      {selectedHorse && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
          onClick={() => { setSelectedHorse(null); setSelectedAssignedGirls(undefined); setEditingCare(false); }}
        >
          <div
            className="bg-white rounded-t-xl sm:rounded-xl max-w-3xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with image */}
            <div className="relative h-64 bg-gradient-to-br from-ggaa-cream to-ggaa-green/10 rounded-t-xl">
              {selectedHorse.imageUrl ? (
                <img
                  src={selectedHorse.imageUrl}
                  alt={selectedHorse.name}
                  className="w-full h-full object-cover rounded-t-xl"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0e9d8" width="100" height="200"/%3E%3C/svg%3E';
                  }}
                />
              ) : (
                <HorsePhotoPlaceholder size="lg" />
              )}
              <button
                type="button"
                onClick={() => { setSelectedHorse(null); setSelectedAssignedGirls(undefined); setEditingCare(false); }}
                className="absolute top-3 right-3 bg-white/90 hover:bg-white text-gray-700 rounded-full w-11 h-11 flex items-center justify-center shadow-lg transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {/* Title */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{selectedHorse.name}</h2>
                  {selectedHorse.breed && (
                    <p className="text-gray-600 mt-1">{selectedHorse.breed}</p>
                  )}
                </div>
                {selectedHorse.age && (
                  <span className="text-lg font-semibold text-ggaa-green">
                    {selectedHorse.age} {selectedHorse.age === 1 ? 'year' : 'years'}
                  </span>
                )}
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-6">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(selectedHorse.status)}`}>
                  {selectedHorse.status}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getDesignationBadgeColor(selectedHorse.designation)}`}>
                  {selectedHorse.designation.charAt(0).toUpperCase() + selectedHorse.designation.slice(1)}
                </span>
                {selectedHorse.stallNumber && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium border bg-ggaa-green text-white border-ggaa-green">
                    Stall {selectedHorse.stallNumber}
                  </span>
                )}
              </div>

              {loadingHorseDetail && (
                <p className="text-sm text-gray-500 mb-4">Loading horse records…</p>
              )}

              {canEditHorseCare(selectedHorse) && !editingCare && (
                <button
                  type="button"
                  disabled={loadingHorseDetail}
                  onClick={() => startEditingHorse()}
                  className="w-full sm:w-auto bg-ggaa-green text-white px-4 py-3 rounded-lg text-sm font-medium min-h-[44px] disabled:opacity-50"
                >
                  Edit horse details
                </button>
              )}

              {editingCare && canEditHorseCare(selectedHorse) ? (
                <DetailSection title="Edit personal horse" icon="edit" accent="green">
                  <FamilyHorseCareEditor
                    horse={selectedHorse}
                    onCancel={() => setEditingCare(false)}
                    onSaved={(patch) => {
                      mergeHorseUpdate(selectedHorse.id, patch);
                      setEditingCare(false);
                    }}
                  />
                </DetailSection>
              ) : (
                <>
                  <DetailSection title="Basic Information" icon="info" accent="green">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      <DetailRow label="Color" value={selectedHorse.color} />
                      <DetailRow label="Temperament" value={selectedHorse.temperament} />
                      <DetailRow label="Riding Level" value={selectedHorse.ridingLevel} />
                      <DetailRow label="Acquisition Date" value={formatDate(selectedHorse.acquisitionDate)} />
                      {selectedHorse.designation === 'personal horse' && (
                        <DetailRow label="Owner" value={selectedHorse.ownerFamily?.familyName} />
                      )}
                    </div>
                  </DetailSection>

                  <DetailSection title="Farrier & Health Records" icon="health" accent="blue">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      <DetailRow label="Farrier Name" value={selectedHorse.farrierName} />
                      <DetailRow label="Last Seen by Farrier" value={formatDate(selectedHorse.lastSeenByFarrier)} />
                      <DetailRow label="Last Coggins" value={formatDate(selectedHorse.lastCoggins)} />
                      <DetailRow label="Last Wormer" value={formatDate(selectedHorse.lastWormer)} />
                      <DetailRow label="Last Rabies" value={formatDate(selectedHorse.lastRabies)} />
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Coggins certificate</p>
                      {selectedHorse.cogginsDocumentUrl ? (
                        <CogginsViewLink
                          horseId={selectedHorse.id}
                          className="inline-flex items-center min-h-[44px] text-sm font-medium text-ggaa-green hover:underline break-all"
                        >
                          {selectedHorse.cogginsDocumentOriginalName || 'View Coggins document'}
                        </CogginsViewLink>
                      ) : (
                        <p className="text-sm text-gray-500">No Coggins document on file yet.</p>
                      )}
                    </div>
                  </DetailSection>
                </>
              )}

              {/* Care Notes */}
              {!editingCare && (selectedHorse.specialNeeds || selectedHorse.medicalNotes) && (
                <DetailSection title="Care Notes" icon="care" accent="amber">
                  <div className="space-y-3">
                    {selectedHorse.specialNeeds && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Special Needs</p>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedHorse.specialNeeds}</p>
                      </div>
                    )}
                    {selectedHorse.medicalNotes && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Medical Notes</p>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedHorse.medicalNotes}</p>
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}

              {/* Assignments */}
              {selectedAssignedGirls && selectedAssignedGirls.length > 0 && (
                <DetailSection title="Assigned Ranch Girls" icon="ranchGirl" accent="purple">
                  <ul className="space-y-1">
                    {selectedAssignedGirls.map(girl => (
                      <li key={girl.id} className="text-sm text-gray-700">
                        {girl.firstName} {girl.lastName}
                        {girl.family && (
                          <span className="text-gray-500 text-xs ml-2">({girl.family.familyName})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </DetailSection>
              )}

              {/* General Notes */}
              {!editingCare && selectedHorse.notes && (
                <DetailSection title="Notes" icon="notes" accent="gray">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedHorse.notes}</p>
                </DetailSection>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5">{value && value.toString().trim() !== '' ? value : <span className="text-gray-400">—</span>}</p>
    </div>
  );
}

type SectionAccent = 'green' | 'blue' | 'amber' | 'purple' | 'gray';

const accentStyles: Record<SectionAccent, { header: string; border: string; iconBg: string }> = {
  green:  { header: 'bg-ggaa-green text-white',          border: 'border-ggaa-green/30',   iconBg: 'bg-white/20' },
  blue:   { header: 'bg-blue-600 text-white',            border: 'border-blue-200',        iconBg: 'bg-white/20' },
  amber:  { header: 'bg-amber-500 text-white',           border: 'border-amber-200',       iconBg: 'bg-white/20' },
  purple: { header: 'bg-purple-600 text-white',          border: 'border-purple-200',      iconBg: 'bg-white/20' },
  gray:   { header: 'bg-gray-700 text-white',            border: 'border-gray-300',        iconBg: 'bg-white/20' },
};

function DetailSection({
  title,
  icon,
  accent,
  children,
}: {
  title: string;
  icon: AppIconName;
  accent: SectionAccent;
  children: React.ReactNode;
}) {
  const styles = accentStyles[accent];
  return (
    <div className={`mb-5 rounded-lg overflow-hidden border ${styles.border} shadow-sm`}>
      <div className={`flex items-center gap-2 px-4 py-2.5 ${styles.header}`}>
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${styles.iconBg}`}>
          <AppIcon name={icon} className="w-4 h-4" strokeWidth={2} />
        </span>
        <h3 className="text-sm font-bold uppercase tracking-wider">{title}</h3>
      </div>
      <div className="bg-white p-4">
        {children}
      </div>
    </div>
  );
} 